import { useQuery, useQueryClient } from 'react-query';
import { api } from '../services/api';
import type { CountRow } from './events';

export type PackageMode = 'in_person' | 'virtual';
export type PackageSection = 'hotel' | 'travel' | 'parking' | 'zoom' | 'subEvents';

export interface InfoPackage {
  key: string;
  name: string;
  nameFr: string | null;
  mode: PackageMode;
  audienceKeys: string[];
  sections: Record<PackageSection, boolean>;
  notesEn: string | null;
  notesFr: string | null;
  publishedUrl: string | null;
  publishedAt: string | null;
  preparedAt: string | null;
  audiences: string[];
  sessions: number;
  gaps: string[];
}

export interface CommonTexts {
  arrivalEn?: string | null;
  arrivalFr?: string | null;
  parkingEn?: string | null;
  parkingFr?: string | null;
  accessibilityEn?: string | null;
  accessibilityFr?: string | null;
}

export interface InfoData {
  packages: InfoPackage[];
  common: CommonTexts;
  saved: boolean;
  audiences: { key: string; name: string; funded: boolean; public: boolean }[];
}

export const SECTION_LABEL: Record<PackageSection, string> = {
  hotel: 'Hotel',
  travel: 'Travel rules',
  parking: 'Parking',
  zoom: 'Zoom links',
  subEvents: 'PI meeting',
};

export type CommsState = 'sent' | 'overdue' | 'soon' | 'planned';
export type CopyStatus = 'none' | 'drafted' | 'approved';

export interface CommsItem {
  id: string;
  audienceKey: string;
  audience: string;
  type: string;
  typeLabel: string;
  label: string;
  customLabel: string | null;
  offsetDays: number;
  sendOn: string | null;
  fixedSendOn: string | null;
  copyStatus: CopyStatus;
  copyLink: string | null;
  values: Record<string, string>;
  sentAt: string | null;
  state: CommsState;
  slots: { key: string; label: string; filled: boolean; value: string | null }[];
  missing: string[];
}

export interface CommsData {
  items: CommsItem[];
  saved: boolean;
  counts: { planned: number; sent: number; overdue: number; missingFacts: number };
  types: Record<string, { label: string; offsetDays: number; slots: string[] }>;
  copyStatuses: Record<CopyStatus, string>;
  audiences: { key: string; name: string }[];
}

export interface RatingRow {
  key: string;
  label: string;
  answered: number;
  mean: number | null;
}

export interface SessionRatings {
  sessionId: string;
  title: string;
  day: string;
  startTime: string;
  responses: number;
  numeric: RatingRow[];
  interest: CountRow[];
  newInformation: CountRow[];
  polls: { question: string; answers: CountRow[]; total: number }[];
}

export interface SessionFeedbackData {
  sessions: SessionRatings[];
  withFeedback: number;
  unassigned: number;
  responses: number;
  answers: { value: string; count: number; sessionId: string | null; guess: string | null }[];
  editions: {
    id: string;
    title: string;
    label: string | null;
    zoomPoll: boolean;
    responses: number;
    sessionId: string | null;
  }[];
  agenda: { id: string; day: string; startTime: string; title: string; zoomLink: boolean }[];
}

export interface StatsExtras {
  responders: {
    responses: number;
    people: number;
    members: number;
    attendedInPerson: number;
    attendedOnline: number;
    noAttendanceRecorded: number;
  };
  bySector: CountRow[];
  membership: { members: number; guests: number; speakers: number; fellowCohorts: CountRow[] };
  bySession: {
    sessionId: string;
    title: string;
    day: string;
    startTime: string;
    responses: number;
    numeric: RatingRow[];
  }[];
  unassignedFeedback: number;
  comments: number;
  themes?: { word: string; count: number }[];
  commentList?: { question: string; text: string }[];
}

export function useEventInfo(eventId: string) {
  return useQuery<InfoData>(['event-info', eventId], async () => (await api.get(`/events/${eventId}/info`)).data.data);
}

export function useEventComms(eventId: string) {
  return useQuery<CommsData>(
    ['event-comms', eventId],
    async () => (await api.get(`/events/${eventId}/info/comms`)).data.data,
  );
}

export function useSessionFeedback(eventId: string) {
  return useQuery<SessionFeedbackData>(
    ['event-session-feedback', eventId],
    async () => (await api.get(`/events/${eventId}/feedback/sessions`)).data.data,
  );
}

export function useInfoRefresh(eventId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries(['event-info', eventId]);
    qc.invalidateQueries(['event-comms', eventId]);
    qc.invalidateQueries(['event-session-feedback', eventId]);
    qc.invalidateQueries(['event-stats', eventId]);
    qc.invalidateQueries(['checklist']);
  };
}

export const shortDay = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-CA', { timeZone: 'UTC', month: 'short', day: 'numeric' });
